import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProfile } from "@/hooks/useProfile";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

interface MessageActionsProps {
  message: {
    id: string;
    original_text: string;
    chat_id: string;
    sender_language: string;
    attachment_url?: string;
  };
  chatId: string;
  allParticipants: any[];
  onMessageUpdated: () => void;
  showDeleteDialog: boolean;
  setShowDeleteDialog: (show: boolean) => void;
  showEditDialog: boolean;
  setShowEditDialog: (show: boolean) => void;
}

export const MessageActions = ({
  message,
  chatId,
  allParticipants,
  onMessageUpdated,
  showDeleteDialog,
  setShowDeleteDialog,
  showEditDialog,
  setShowEditDialog,
}: MessageActionsProps) => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [editText, setEditText] = useState(message.original_text);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);

  const handleDelete = async () => {
    if (!user) return;
    setDeleting(true);
    try {
      // Delete associated reactions first
      await supabase
        .from("message_reactions")
        .delete()
        .eq("message_id", message.id);

      const { error } = await supabase
        .from("messages")
        .delete()
        .eq("id", message.id)
        .eq("sender_id", user.id);

      if (error) throw error;

      toast({
        title: t("success"),
        description: t("messageDeleted"),
      });
      onMessageUpdated();
    } catch (error) {
      console.error("Error deleting message:", error);
      toast({
        title: t("error"),
        description: t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleEdit = async () => {
    if (!user || !profile || !editText.trim()) return;
    setEditing(true);
    try {
      // Re-translate the edited message
      let translatedTexts: Record<string, string> = {};

      const targetLanguages = [
        ...new Set(
          allParticipants
            .filter((p: any) => Boolean(p?.id && p?.preferred_language))
            .filter(
              (p: any) =>
                p.id !== user.id &&
                p.preferred_language !== profile.preferred_language
            )
            .map((p: any) => p.preferred_language)
        ),
      ];

      if (targetLanguages.length > 0 && editText.trim()) {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          const accessToken = session?.access_token;

          const { data: translationData, error: translationError } =
            await supabase.functions.invoke("translate-message", {
              headers: accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : undefined,
              body: {
                text: editText.trim(),
                sourceLanguage: profile.preferred_language ?? language,
                targetLanguages,
              },
            });

          if (!translationError && translationData?.translations) {
            translatedTexts = translationData.translations;
          }
        } catch (err) {
          console.error("Translation error during edit:", err);
        }
      }

      const { error } = await supabase
        .from("messages")
        .update({
          original_text: editText.trim(),
          translated_texts: translatedTexts,
        })
        .eq("id", message.id)
        .eq("sender_id", user.id);

      if (error) throw error;

      toast({
        title: t("success"),
        description: t("messageEdited"),
      });
      onMessageUpdated();
    } catch (error) {
      console.error("Error editing message:", error);
      toast({
        title: t("error"),
        description: t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setEditing(false);
      setShowEditDialog(false);
    }
  };

  return (
    <>
      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteMessage")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteMessageConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("editMessage")}</DialogTitle>
            <DialogDescription>{t("editMessageHint")}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="min-h-[100px]"
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              disabled={editing}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleEdit}
              disabled={editing || !editText.trim()}
            >
              {editing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
